import { formatDueNote } from "logic/utils";
import Link from "next/link";
import { AmountTooltip, ButtonDS, Icon, Skeleton } from "ui/components";

import type { Loan } from "./data/types";
import { useNow } from "./NowProvider";
import { isQuoteQuarantined } from "./quarantinedQuotes";
import { useLoanTimestamps } from "./useLoanTimestamp";
import { useRepayInfo } from "./useRepayInfo";

type Props = {
  loan: Loan;
};

export function OverdueAlert(props: Props) {
  const { loan } = props;

  const { isRepayment } = useLoanTimestamps(loan);
  const now = useNow();
  const { data } = useRepayInfo(loan, isRepayment);

  const quote = isQuoteQuarantined(loan) ? null : data;

  const collateral = loan.metadata.name ?? "-";

  return (
    <div
      className={`
        flex flex-col gap-3 rounded-button border border-sys-negative-negative bg-surface-surface px-4 py-3
        text-sys-negative-negative
        md:flex-row md:items-center md:gap-2
      `}
    >
      <div className="flex gap-2 md:flex-1">
        {/* Top-aligned with the title line rather than centred on the whole block. */}
        <Icon name="alert_filled" className="shrink-0 self-start" />

        <div className="flex flex-1 flex-col gap-1">
          <p className="text-body-lg-500">Payment Overdue</p>

          {/* A `div`, not a `p`: `AmountTooltip` renders a block-level trigger,
              which is invalid inside a paragraph. Spelling the amount's three
              states out inline rather than using `<Ty>` for the same reason —
              it wraps node values in a `div` and would break the sentence. */}
          <div className="text-body-lg-400">
            {collateral} payment of <OverdueAmount decimals={loan.erc20.decimals} quote={quote} /> is{" "}
            {formatDueNote(loan.nextDeadline, now)}.
          </div>
        </div>
      </div>

      <ButtonDS
        as={Link}
        className="w-full md:w-auto"
        href={`/loans?loan=${loan.loanTermsHash}`}
        size="md"
        variant="destructive"
      >
        Pay now
      </ButtonDS>
    </div>
  );
}

type OverdueAmountProps = {
  /** The repayment quote, keeping its three states distinct: a value, `null`
   *  when quarantined, `undefined` while it loads. */
  quote: { total: bigint } | null | undefined;
  decimals: number;
};

function OverdueAmount(props: OverdueAmountProps) {
  const { quote, decimals } = props;

  // `null` and `undefined` must branch separately — a quarantined loan has no
  // quote to wait for, so folding it into the loading case would leave the
  // sentence skeletonised forever.
  if (quote === null) return <>--</>;

  // Fixed width: `Skeleton` is `flex w-fit`, so with no width it collapses to
  // nothing mid-sentence.
  if (quote === undefined) return <Skeleton className="inline-flex h-4 w-24 align-middle" />;

  return <AmountTooltip amount={quote.total} decimals={decimals} />;
}
